import { stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { INLINE_CTA } from "../../lib/blog-markers";
import { no, partial, unstated, yes } from "../comparatifs/cells";

export const meilleureListeAnniversaire: BlogPost = {
  h1: "Les meilleures listes d'anniversaire en ligne en 2026",
  seoTitle: "Meilleure liste d'anniversaire en ligne 2026 : le comparatif · Listou",
  seoDescription:
    "Listou, Mes Envies, Milirose, Listy, Envy, Youpili, Lalaliste, Kadolog : prix, surprise, gestion à plusieurs, design de la liste et cagnotte. Le comparatif des listes d'anniversaire, sources à l'appui.",
  publishedOn: "2026-09-29",
  updatedOn: "2026-09-29",
  checkedOn: "2026-09-29",
  summary:
    "Presque toutes les listes d'anniversaire en ligne sont gratuites, acceptent n'importe quelle boutique et laissent les invités réserver sans compte. Elles se distinguent sur la surprise (la personne fêtée voit-elle ce qui est réservé ?), la gestion à plusieurs, la cagnotte, et le soin apporté à la page elle-même. Listou, notre service, garde la surprise par défaut, se gère à deux et se personnalise entièrement : modèle de page, palette, police, stickers.",
  table: {
    caption: "Huit listes d'anniversaire en ligne, critère par critère",
    columns: [
      "Prix",
      "Compte pour réserver",
      "Surprise",
      "Gestion à plusieurs",
      "Personnalisation",
      "Argent",
    ],
    rows: [
      {
        name: "Listou",
        cells: [
          yes("Gratuit, sans limite de réservations"),
          yes("Aucun, prénom et email facultatifs"),
          yes("Par défaut ; se coupe en un clic"),
          yes("Co-propriétaire, invités qui modifient"),
          yes("6 modèles, 13 palettes, 4 polices, 162 stickers, motifs"),
          partial("Lien vers votre cagnotte"),
        ],
      },
      {
        name: "Mes Envies",
        cells: [
          yes("Gratuit"),
          yes("Aucun"),
          no("Le créateur suit les réservations"),
          unstated("Non décrit"),
          partial("Photo d'en-tête, couleur des boutons"),
          yes("Virement ou cagnotte, sans frais"),
        ],
      },
      {
        name: "Milirose",
        cells: [
          yes("Gratuit"),
          yes("Aucun"),
          partial("Trois niveaux, « Sans surprise » par défaut"),
          unstated("Non décrit"),
          partial("Image d'en-tête, couleurs du menu et des boutons"),
          yes("RIB ou PayPal, sans commission"),
        ],
      },
      {
        name: "Listy",
        cells: [
          yes("Gratuit"),
          yes("Aucun, un email pour annuler"),
          yes("Au choix à la création"),
          yes("« Gérez votre liste à plusieurs »"),
          partial("Texte de présentation et photos"),
          partial("Lien Leetchi, Lydia, PayPal…"),
        ],
      },
      {
        name: "Envy",
        cells: [
          yes("Gratuit, sans publicité"),
          yes("Aucun"),
          yes("Au choix : voir ou masquer les réservations"),
          yes("Listes collaboratives"),
          unstated("Non décrit"),
          partial("Lien de cagnotte externe"),
        ],
      },
      {
        name: "Youpili",
        cells: [
          yes("Gratuit"),
          unstated("Non précisé"),
          no("Le créateur voit qui a réservé quoi"),
          yes("Co-créateur"),
          partial("Photo de couverture, nom, message d'accueil"),
          yes("Don libre, sans frais cachés"),
        ],
      },
      {
        name: "Lalaliste",
        cells: [
          yes("Gratuit"),
          yes("Aucun, anonymat possible"),
          no("Le créateur voit qui réserve"),
          unstated("Non décrit"),
          yes("Couleurs, thèmes, photos, messages"),
          unstated("Non décrit"),
        ],
      },
      {
        name: "Kadolog",
        cells: [
          partial("Gratuit jusqu'à 10 réservations, Premium 29 €"),
          unstated("Non décrit"),
          no("Les réservations s'affichent dans « mes cadeaux reçus »"),
          unstated("Non décrit"),
          partial("Photo de couverture, titre, fond d'écran"),
          yes("Premium : virement, ou carte à 2,4 %"),
        ],
      },
    ],
  },
  body: `
## Comment nous avons comparé

Nous avons retenu les services de liste d'anniversaire les plus visibles en France, pour adultes comme pour enfants. Listou est notre service : nous le présentons avec les autres, et chaque information sur les autres vient de leur propre site, listé en bas de page avec la date de consultation. Quand un site ne décrit pas une fonction, nous l'écrivons « non décrit » plutôt que de supposer qu'elle n'existe pas.

## Ce qui compte pour un anniversaire

- **La surprise.** Pour ses propres cadeaux, on préfère souvent ne pas savoir. Pour la liste d'un enfant de 6 ans, au contraire, le parent veut voir ce qui est pris pour ne pas acheter la même chose. Le bon réglage dépend de la liste : il doit se changer facilement.
- **La gestion à plusieurs.** Les deux parents remplissent la liste de l'enfant ; une marraine veut ajouter une idée.
- **Le design.** Une liste d'anniversaire s'envoie dans le groupe WhatsApp de la classe ou s'imprime sur une invitation. Elle donne le ton de la fête.
- **L'argent.** Pour un vélo ou une console, plusieurs invités participent.

## Listou : la liste qui ressemble à la fête

**Listou** est gratuit, sans limite de réservations. On colle le lien d'un produit de n'importe quelle boutique : le nom, la photo et le prix sont récupérés. Les invités réservent sans compte, le prénom et l'email sont facultatifs.

C'est sur le design que Listou va le plus loin. Dans l'onglet Apparence, avec l'aperçu en direct, vous choisissez parmi **6 modèles de page** (la mise en page elle-même change), **13 palettes** et une couleur d'accent, **4 polices** de titre (ronde, élégante, manuscrite, éditoriale), et **162 stickers** dessinés en 18 thèmes, pour décorer l'en-tête ou tapisser le fond en motifs. Une photo de couverture et un emoji complètent la page. Un anniversaire de pirate et les 40 ans d'une amie n'ont pas la même liste. La liste se partage par lien, par **QR code** à imprimer sur l'invitation, ou en **visuels prêts pour Instagram**.

Le mode surprise est activé par défaut : vous ne voyez ni ce qui est réservé, ni par qui, jusqu'au lendemain de l'anniversaire. Pour la liste d'un enfant que vous gérez, il se coupe en un clic, et vous voyez ce qui est pris. Le co-parent devient **co-propriétaire**, et vous pouvez inviter la marraine à « modifier » la liste.

Pour les frères et sœurs, ou des jumeaux, un **groupe** réunit plusieurs listes derrière un seul lien : chaque enfant a la sienne, et les invités passent de l'une à l'autre.

Sa limite : Listou ne fait transiter aucun argent. Pour un gros cadeau commun, vous ajoutez un lien vers votre cagnotte (Leetchi, Lydia…), sans champ pour un IBAN.
${INLINE_CTA}## Mes Envies : les participations sans frais

**Mes Envies** est gratuit et accepte n'importe quelle enseigne, en ligne ou en magasin, y compris par photo. Les invités réservent sans compte. Les participations en argent vont directement sur votre compte, sans frais ni pour vous ni pour l'invité. Les invités peuvent aussi laisser un message vidéo, que vous téléchargez en souvenir.

Le créateur suit les cadeaux réservés, et l'aide ne décrit pas de réglage pour les cacher. La personnalisation se limite à une photo d'en-tête et à la couleur des liens et boutons.

## Milirose : trois niveaux de surprise

**Milirose** est gratuit, accepte n'importe quel site marchand et laisse réserver sans compte. Il propose trois niveaux de surprise : « Sans surprise », le réglage par défaut, « Demi-surprise » et « Surprise totale », modifiables à tout moment.

Pour l'argent, un champ accueille votre RIB ou votre compte PayPal, sans commission Milirose ; la participation partielle est possible sur les cadeaux de plus de 25 €. La personnalisation porte sur l'image d'en-tête et les couleurs du menu et des boutons. La FAQ ne décrit pas de gestion à plusieurs.

## Listy : la surprise au choix et la liste à plusieurs

**Listy** est gratuit et se finance par des commissions d'affiliation, sans effet sur les prix. On ajoute les articles de n'importe quel site marchand, notamment avec une extension Chrome ou Firefox. Les invités réservent sans compte, avec un email pour recevoir un lien d'annulation.

À la création, vous choisissez de garder ou non la surprise : en mode surprise, vous êtes prévenu d'une réservation sans savoir laquelle ni par qui. Listy annonce aussi qu'on peut gérer sa liste à plusieurs. La personnalisation passe par un texte de présentation et des photos. Pour l'argent, on ajoute une cagnotte Leetchi, Lydia ou PayPal.

## Envy : la surprise au choix, sans publicité

**Envy** est gratuit, sans publicité. On colle un lien, ou on photographie un article vu en magasin. Les invités réservent sans compte, et vous choisissez de voir les cadeaux réservés ou de garder la surprise complète. Des fils de commentaires cachés permettent aux invités de s'organiser, et les listes collaboratives laissent plusieurs personnes ajouter des idées et voter. Envy fonctionne dans le navigateur, avec une extension Chrome et une appli Android. Pour l'argent, on ajoute le lien d'une cagnotte externe.

## Youpili : un catalogue intégré

**Youpili** est gratuit et intègre un catalogue de plus d'un million de références avec un comparateur de prix, en plus des articles de n'importe quelle boutique. Un co-créateur peut gérer la liste avec vous, et un « don libre » permet de recevoir de l'argent, sans frais cachés. La page se personnalise avec une photo de couverture, un nom, la date et un message d'accueil.

Côté surprise, Youpili met en avant le suivi en temps réel de qui a réservé quel cadeau.

## Lalaliste : des applis et des thèmes

**Lalaliste** est gratuit, sur Android, iPhone et le web. On ajoute des articles de n'importe quel site, ou par photo en magasin. Les invités réservent sans compte et peuvent rester anonymes. La liste se personnalise avec des couleurs, des thèmes, des photos et des messages, et se partage par email, SMS, réseaux sociaux ou dans une invitation. Le créateur voit en temps réel qui réserve ou achète quoi.

## Kadolog : le paiement par carte, en Premium

**Kadolog** accepte n'importe quelle boutique. Sa formule gratuite a une limite : d'après la FAQ, au-delà de 10 réservations, les invités ne peuvent plus réserver sans Premium, à 29 € par liste. Premium permet de recevoir l'argent des cadeaux par virement, sans frais, ou par carte, avec 2,4 % de frais. Le créateur voit les cadeaux réservés dans « mes cadeaux reçus ».

## Quelle liste choisir ?

- **Vous voulez une liste à l'image de la fête** : Listou, avec ses modèles, palettes, polices et stickers. Lalaliste propose aussi des thèmes et des couleurs.
- **Vous voulez être surpris** : Listou, où la surprise est active par défaut, ou Listy, Envy et Milirose, en choisissant le bon réglage.
- **Vous préparez la liste d'un enfant à deux** : Listou (co-propriétaire), Youpili (co-créateur) ou Listy.
- **Plusieurs enfants, un seul lien** : les groupes de Listou.
- **Vous voulez recevoir de l'argent directement** : Mes Envies, Milirose ou Youpili ; Kadolog en Premium pour la carte bancaire.

Pour vous lancer, la [liste d'anniversaire Listou](/liste-anniversaire) est prête en deux minutes. Nos comparatifs détaillés opposent aussi Listou à [Mes Envies](/comparatif/mesenvies), [Milirose](/comparatif/milirose) et [Kadolog](/comparatif/kadolog).
`,
  faq: [
    [
      "Quelle est la meilleure liste d'anniversaire en ligne en 2026 ?",
      "Cela dépend de ce qui compte pour vous. Pour une liste qui se personnalise entièrement, garde la surprise et se gère à deux, Listou. Pour recevoir des participations directement sur votre compte, Mes Envies, Milirose ou Youpili. Pour un catalogue intégré, Youpili. Pour des applis mobiles, Lalaliste.",
    ],
    [
      "Quelle liste d'anniversaire est gratuite ?",
      "Listou, Mes Envies, Milirose, Listy, Envy, Youpili et Lalaliste sont gratuits. Kadolog a une formule gratuite, mais au-delà de 10 réservations les invités ne peuvent plus réserver sans Premium, à 29 € par liste.",
    ],
    [
      "Peut-on personnaliser le design de sa liste d'anniversaire ?",
      "Sur Listou, entièrement : 6 modèles de page, 13 palettes, 4 polices, 162 stickers, fonds à motifs et photo de couverture. Lalaliste propose des couleurs et des thèmes. Mes Envies, Milirose, Youpili, Listy et Kadolog permettent surtout une photo d'en-tête, un texte ou des couleurs de boutons.",
    ],
    [
      "Sur quelle liste la personne fêtée ne voit-elle pas les cadeaux réservés ?",
      "Sur Listou, par défaut. Sur Listy et Envy, en choisissant la surprise à la création, et sur Milirose avec le niveau « Surprise totale ». Sur Mes Envies, Youpili, Lalaliste et Kadolog, le créateur suit les réservations.",
    ],
    [
      "Comment faire la liste d'anniversaire d'un enfant ?",
      "Créez la liste à son nom et coupez la surprise pour voir ce qui est pris. Sur Listou, le co-parent devient co-propriétaire, et un groupe réunit les listes de plusieurs enfants derrière un seul lien. Imprimez le QR code de la liste sur l'invitation.",
    ],
    [
      "Les invités doivent-ils créer un compte pour réserver ?",
      "Non sur Listou, Mes Envies, Milirose, Listy, Envy et Lalaliste. Sur Listou, le prénom et l'email sont même facultatifs.",
    ],
    [
      "Comment faire un cadeau commun pour un anniversaire ?",
      "Mes Envies et Milirose reçoivent des participations directement sur votre compte, sans frais ; Youpili propose un don libre ; Kadolog Premium encaisse par carte à 2,4 %. Listou, Listy et Envy ajoutent un lien vers une cagnotte externe.",
    ],
  ],
  sources: [
    {
      label: "Mes Envies : liste d'anniversaire",
      url: "https://www.mesenvies.fr/liste-anniversaire.html",
    },
    {
      label: "Mes Envies : gérer les réservations et dons libres",
      url: "https://aide.mesenvies.fr/hc/331138012/5/gerer-les-reservations-et-dons-libres",
    },
    {
      label: "Mes Envies : personnaliser l'aspect visuel de votre liste",
      url: "https://aide.mesenvies.fr/hc/331138012/7/personnaliser-laspect-visuel-de-votre-liste",
    },
    { label: "Milirose : foire aux questions", url: "https://www.milirose.com/faq.html" },
    { label: "Listy : liste d'anniversaire", url: "https://www.listy.fr/liste-d-anniversaire" },
    { label: "Listy : aide", url: "https://www.listy.fr/aide" },
    { label: "Envy : page d'accueil", url: "https://envynest.fr/" },
    { label: "Youpili : liste d'anniversaire", url: "https://youpili.fr/liste-anniversaire" },
    { label: "Lalaliste : liste d'anniversaire", url: "https://lalaliste.fr/liste-danniversaire/" },
    {
      label: "Kadolog : tarifs et conditions",
      url: "https://www.kadolog.com/fr-fr/tarifs-et-conditions",
    },
    { label: "Kadolog : FAQ", url: "https://www.kadolog.com/fr-fr/faq" },
    {
      label: "Kadolog : FAQ des détenteurs d'une liste",
      url: "https://www.kadolog.com/fr-fr/faq/45203",
    },
  ],
  occasion: "birthday",
  inlineCta: {
    title: "Une liste qui ressemble à la fête",
    text: "Un lien ou un QR code dans l'invitation, et chaque invité sait quoi offrir.",
    sticker: stickerUrl("fete", "ballons"),
  },
};
